import React, {useState, useEffect, useRef} from "react";
import {Controller} from "react-hook-form";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import {Loader, MinLoader} from "../loaders";
import {Svg} from "assets";
import {getNestedError, usePaginatedFetch} from "utils";

export const SelectAutocompletePagination = ({
                                                 control,
                                                 name,
                                                 rules,
                                                 labelHeader,
                                                 placeholder,
                                                 renderLabel,
                                                 renderOption,
                                                 classNameWrapper,
                                                 disabled,
                                                 disableClearable,
                                                 StartIcon,
                                                 allowError = true,
                                                 handleChange = () => {
                                                 },
                                                 CustomLastElement,
                                                 filterOptions,
                                                 selectedValue,
                                                 async = false,
                                                 apiUrl,
                                                 paramsBuilder,
                                                 sendParams = {},
                                                 limit = 50,
                                                 refreshable = true,
                                             }) => {
    const inputRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [refreshClicked, setRefreshClicked] = useState(false);

    /** Hook always called to respect rules-of-hooks */
    const {data, options, setOptions, page, setPage, hasMore, loading, search, setSearch, fetchData} =
        usePaginatedFetch({apiUrl, paramsBuilder, sendParams, limit});

    /** Initial fetch or page/search change */
    useEffect(() => {
        if (async && apiUrl) fetchData(page === 1);
    }, [page, search, apiUrl, async]);

    /** Refresh click */
    const handleRefresh = () => {
        setRefreshClicked(true);
        setPage(1);
        setSearch("");
        setOptions([]);
        fetchData(true);
    };

    /** Infinite scroll */
    const handleScroll = (event) => {
        const listBoxNode = event.currentTarget;
        if (listBoxNode.scrollTop + listBoxNode.clientHeight === listBoxNode.scrollHeight) {
            if (hasMore && !loading) {
                setPage(prev => prev + 1);
                fetchData();
            }
        }
    };

    const optionsList = async ? options : sendParams?.list || [];

    return (
        <div className={`input-main-wrapper input-custom-wrapper ${classNameWrapper || ""}`}>
            <Controller
                control={control}
                name={name}
                rules={rules}
                render={({field, formState: {errors}}) => {
                    const error = getNestedError(errors, name);
                    const hasError = !!error?.type;

                    if (hasError && inputRef.current) {
                        inputRef.current.scrollIntoView({behavior: "smooth", block: "center"});
                    }

                    const selectedOption = selectedValue
                        ? optionsList?.find((item) => item?.[selectedValue] === field.value) || null
                        : field.value ?? null;

                    return (
                        <>
                            {labelHeader && (
                                <p className={`input-main-label-header ${hasError ? "errorLabel" : ""}`}>
                                    {labelHeader}
                                    {rules?.required && <span className="input-main-label-header-required">*</span>}
                                </p>
                            )}

                            <Autocomplete
                                {...field}
                                disabled={disabled}
                                disableClearable={disableClearable}
                                open={open}
                                onOpen={() => setOpen(true)}
                                onClose={(event) => {
                                    if (refreshClicked) {
                                        event.preventDefault();
                                        setRefreshClicked(false);
                                        return;
                                    }
                                    setOpen(false);
                                    setSearch("");
                                    if (async && !optionsList.length) fetchData(true);
                                }}
                                filterOptions={filterOptions}
                                popupIcon={loading ? (
                                    <Loader
                                        styles={{
                                            width: 15,
                                            minWidth: 15,
                                            height: 15,
                                            minHeight: 15,
                                        }}
                                    />
                                ) : <Svg.SelectArrowSvg/>}
                                options={refreshable ? [...optionsList, {
                                    id: "__REFRESH__",
                                    name: "Refresh"
                                }] : optionsList}
                                value={selectedOption}
                                getOptionLabel={(option) => option?.id !== "__REFRESH__" && renderLabel(option) ? renderLabel(option) : ""}
                                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                slotProps={{
                                    listbox:{
                                        onScroll:async ? handleScroll : undefined
                                    }
                                }}
                                onChange={(event, item) => {
                                    if (item?.id === "__REFRESH__") handleRefresh();
                                    else {
                                        const newValue = item ? (selectedValue ? item[selectedValue] : item) : null;
                                        field.onChange(newValue);
                                        handleChange(newValue);
                                        setOpen(false);
                                        document?.activeElement?.blur();
                                    }
                                }}
                                renderOption={(props, option, {selected}) => {
                                    const isRefresh = option?.id === "__REFRESH__";
                                    const lastElement = option?.id === optionsList[optionsList.length - 1]?.id;

                                    if (isRefresh) {
                                        return (
                                            <li {...props} className="refresh-btn-wrapper">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleRefresh();
                                                    }}
                                                    className="refresh-btn"
                                                >
                                                    <div className="refresh-btn-image-box">
                                                        {loading ? (
                                                            <MinLoader
                                                                height={15}
                                                                width={15}
                                                                color={"rgb(255, 193, 7)"}
                                                                margin={"0"}
                                                                position={"relative"}
                                                            />
                                                        ) : (
                                                            <Svg.Refresh2Svg style={{
                                                                width: 15,
                                                                height: 15,
                                                                filter: "brightness(0) saturate(100%) invert(86%) sepia(16%) saturate(6697%) hue-rotate(354deg) brightness(101%) contrast(101%)"
                                                            }}/>
                                                        )}
                                                    </div>
                                                    <p>Refresh</p>
                                                </button>
                                            </li>
                                        );
                                    }

                                    return (
                                        <>
                                            <li
                                                {...props}
                                                key={option?.id || option?.value}
                                                className={`main-select-option ${selected ? "main-select-option-selected" : ""}`}
                                                style={option?.style ? option?.style : {}}
                                            >
                                                {option?.Icon &&
                                                    <option.Icon className="main-select-option-round-icon"/>}
                                                {renderOption(option)}
                                            </li>
                                            {lastElement && CustomLastElement && (
                                                <li className="select-custom-last-element-wrapper">{CustomLastElement}</li>
                                            )}
                                        </>
                                    );
                                }}
                                renderInput={(params) => {
                                    const IconStart = StartIcon;
                                    return (
                                        <TextField
                                            {...params}
                                            onChange={(e) => {
                                                if (async) {
                                                    setSearch(e.target.value);
                                                    setPage(1);
                                                    setOptions([]);
                                                }
                                            }}
                                            placeholder={placeholder}
                                            inputRef={(el) => {
                                                field.ref(el);
                                                inputRef.current = el;
                                            }}
                                            error={hasError}
                                            InputProps={IconStart ? {
                                                ...params.InputProps,
                                                startAdornment: <IconStart/>
                                            } : params.InputProps}
                                        />
                                    );
                                }}
                            />

                            {allowError && <p className="error-message">{error?.message}</p>}
                        </>
                    );
                }}
            />
        </div>
    );
};
